/**
 * The live transcript as its own panel, under the phone's controls rather than inside them.
 *
 * Reading and operating are different activities: the call view is a control surface sized for
 * buttons, and a conversation renders as a column of text that wants its own scrollback —
 * growing it inside the phone pushed the hangup button around mid-call. So the transcript is a
 * second section of the phone's popover, under the controls, with a scroll area of its own: it
 * appears when there is a transcript to show, and it folds to its header when the rep wants it
 * out of the way.
 *
 * The state cannot live in either one. The toggle sits in the dock's call controls (it is a call
 * action, next to mute and hold), the lines render here, and both must survive the other
 * unmounting — the popover closes, this panel folds, neither may drop the SSE subscription. So
 * `TranscriptionProvider` owns the `useTranscription` hook above both, keyed to the phone's
 * active call, and each reads the same context.
 */

import { Captions, ChevronDown, ChevronUp } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { Callout } from "@/components/ui/feedback";
import { Tooltip } from "@/components/ui/misc";
import { cn } from "@/lib/utils";
import { useSoftphone } from "@/softphone/softphone-context";
import type { TranscriptLine } from "@/softphone/transcription";
import { useTranscription, type TranscriptionState } from "@/softphone/use-transcription";

export interface TranscriptionContextValue extends TranscriptionState {
  /** The far party of the call being transcribed, for the panel's header. */
  remoteLabel: string | null;
}

/** Exported for the dev-time state gallery only; the app goes through `TranscriptionProvider`. */
export const TranscriptionContext = React.createContext<TranscriptionContextValue | null>(null);

export function TranscriptionProvider({ children }: { children: React.ReactNode }) {
  const phone = useSoftphone();
  const activeCall = phone.calls.find((c) => c.id === phone.activeCallId) ?? null;
  const transcription = useTranscription(activeCall);

  const value = React.useMemo(
    () => ({
      ...transcription,
      remoteLabel: activeCall ? (activeCall.remoteName ?? activeCall.remote) : null,
    }),
    [transcription, activeCall],
  );

  return (
    <TranscriptionContext.Provider value={value}>{children}</TranscriptionContext.Provider>
  );
}

/** The dock's toggle and this panel read the same state; outside the provider is a wiring
 *  mistake worth failing loudly on, not a state to render around. */
export function useCallTranscription(): TranscriptionContextValue {
  const value = React.useContext(TranscriptionContext);
  if (!value) {
    throw new Error("useCallTranscription must be used within TranscriptionProvider");
  }
  return value;
}

/**
 * The transcript section of the phone's popover. Renders only when there is something to show —
 * live, starting, failed, or holding lines worth reading (they survive stopping, until the next
 * call replaces them).
 *
 * Only on a desktop-width window, which `SoftphoneDock` decides: on a narrow one the phone floats
 * over the page, and a transcript there would cover what the phone does not. A phone-sized
 * transcript layout is its own design task, not a media query.
 */
export function TranscriptPanel() {
  const state = useCallTranscription();
  const [folded, setFolded] = React.useState(false);

  const hasContent = state.finals.length > 0 || state.partials.length > 0;
  const visible = state.on || state.pending || state.error !== null || hasContent;
  if (!visible) return null;

  return (
    <section aria-label="Live transcript" className="flex shrink-0 flex-col border-t border-border">
      <div
        className={cn(
          "flex h-9 shrink-0 items-center gap-2 px-3",
          !folded && "border-b border-border",
        )}
      >
        <Captions className="size-3.5 shrink-0 text-accent-11" aria-hidden />
        <span className="text-xs font-medium text-text">Live transcript</span>
        {state.remoteLabel && (
          <span className="min-w-0 flex-1 truncate font-mono text-2xs text-text-muted">
            {state.remoteLabel}
          </span>
        )}
        {state.on && (
          <span className="relative flex size-2 shrink-0" aria-label="Transcribing">
            <span className="absolute inline-flex size-full animate-ping rounded-full bg-success-9 opacity-60" />
            <span className="relative inline-flex size-2 rounded-full bg-success-9" />
          </span>
        )}
        <Tooltip content={folded ? "Expand" : "Fold away"}>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => setFolded((v) => !v)}
            aria-expanded={!folded}
            aria-label={folded ? "Expand transcript" : "Fold transcript away"}
          >
            {folded ? <ChevronDown /> : <ChevronUp />}
          </Button>
        </Tooltip>
      </div>

      {!folded && (
        <div className="p-3">
          {state.error && (
            <Callout tone="danger" className="mb-2 text-2xs">
              {state.error}
            </Callout>
          )}
          <TranscriptLines state={state} />
        </div>
      )}
    </section>
  );
}

/**
 * Finals accumulate; the in-flight guess per speaker renders dimmed underneath and is replaced
 * in place — the reducer in `transcription.ts` owns that rule. Pinned to the bottom the way a
 * chat is: the newest line is the one being spoken.
 */
function TranscriptLines({ state }: { state: TranscriptionState }) {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [state.finals.length, state.partials]);

  return (
    <div
      ref={scrollRef}
      className="max-h-56 space-y-1 overflow-y-auto text-xs scrollbar-subtle"
      aria-live="polite"
    >
      {state.finals.length === 0 && state.partials.length === 0 && (
        <p className="italic text-text-subtle">Listening…</p>
      )}
      {state.finals.map((line) => (
        <TranscriptRow key={line.key} line={line} />
      ))}
      {state.partials.map((line) => (
        <TranscriptRow key={`partial-${line.key}`} line={line} dim />
      ))}
    </div>
  );
}

function TranscriptRow({ line, dim = false }: { line: TranscriptLine; dim?: boolean }) {
  return (
    <p className={dim ? "italic opacity-60" : undefined}>
      {line.at && (
        <span className="mr-1.5 font-mono text-2xs tabular text-text-subtle">{line.at}</span>
      )}
      {line.label && (
        <span
          className={cn(
            "mr-1 font-medium",
            line.role === "near" ? "text-accent-11" : "text-success-11",
          )}
        >
          {line.label}:
        </span>
      )}
      <span className="text-text">{line.text}</span>
    </p>
  );
}
